import { keyBy } from "lodash";
import { Key, useCallback, useMemo } from "react";
import { useImmer } from "use-immer";

export interface UseSelectionResult<T> {
  count: number;
  anySelected: boolean;
  allSelected: boolean;
  values(): readonly T[];
  select(option: T): void;
  selectAll(): void;
  deselect(option: T): void;
  deselectAll(): void;
  toggle(option: T): void;
  toggleAll(): void;
  includes(option: T): boolean;
  includesAny(options: readonly T[]): boolean;
  includesAll(options: readonly T[]): boolean;
}

const useSelection = <T>(
  options: readonly T[],
  getKey: (option: T) => Key
): UseSelectionResult<T> => {
  const [selectedKeys, updateSelectedKeys] = useImmer<ReadonlySet<Key>>(
    new Set()
  );

  // lodash's keyBy iteratee overload doesn't accept a return type as wide as
  // React's Key (which includes bigint); no caller actually returns bigint.
  const optionsByKey = useMemo<Record<string, T>>(
    () => keyBy([...options], getKey as (option: T) => string | number),
    [options, getKey]
  );

  const count = selectedKeys.size;
  const anySelected = selectedKeys.size > 0;
  const allSelected = selectedKeys.size === options.length;

  const values = useCallback(
    () => [...selectedKeys].map((key) => optionsByKey[String(key)]),
    [selectedKeys, optionsByKey]
  );

  const select = useCallback(
    (option: T) => updateSelectedKeys((keys) => void keys.add(getKey(option))),
    [updateSelectedKeys, getKey]
  );

  const selectAll = useCallback(
    () => updateSelectedKeys(() => new Set(options.map(getKey))),
    [options, updateSelectedKeys, getKey]
  );

  const deselect = useCallback(
    (option: T) =>
      updateSelectedKeys((keys) => void keys.delete(getKey(option))),
    [updateSelectedKeys, getKey]
  );

  const deselectAll = useCallback(
    () => updateSelectedKeys(() => new Set()),
    [updateSelectedKeys]
  );

  const includes = useCallback(
    (option: T) => selectedKeys.has(getKey(option)),
    [selectedKeys, getKey]
  );

  const includesAny = useCallback(
    (options: readonly T[]) => options.some(includes),
    [includes]
  );
  const includesAll = useCallback(
    (options: readonly T[]) => options.every(includes),
    [includes]
  );

  const toggle = useCallback(
    (option: T) => (includes(option) ? deselect(option) : select(option)),
    [includes, deselect, select]
  );

  const toggleAll = useCallback(
    () => (allSelected ? deselectAll() : selectAll()),
    [allSelected, selectAll, deselectAll]
  );

  return useMemo(
    () => ({
      count,
      anySelected,
      allSelected,
      values,
      select,
      selectAll,
      deselect,
      deselectAll,
      toggle,
      toggleAll,
      includes,
      includesAny,
      includesAll,
    }),
    [
      count,
      anySelected,
      allSelected,
      values,
      select,
      selectAll,
      deselect,
      deselectAll,
      toggle,
      toggleAll,
      includes,
      includesAny,
      includesAll,
    ]
  );
};

export default useSelection;
